import React, { useEffect, useMemo, useState } from "react";
import { isNotificationUnread } from "../../utils/notificationUnread";
import { useSearchParams, useNavigate } from "react-router-dom";
import DoctorLayout from "../../Layout/DoctorLayout";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { getSocket } from "../../Api/socket";
import { SkeletonBlock, SkeletonText } from "../../components/Skeleton/Skeleton";
import { getProviderNotificationPathOrDefault } from "../../utils/notificationRoutes";
import "./DoctorNotifications.scss";

function formatTimeAgo(date) {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "—";
  const now = Date.now();
  const ms = now - d.getTime();
  const mins = Math.floor(ms / (60 * 1000));
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min${mins !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hr${hours !== 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function getTypeBadge(type) {
  const t = String(type || "").toLowerCase();
  if (t === "match_request_live") return { label: "New request", cls: "new" };
  if (t === "new_bid") return { label: "New bid", cls: "new" };
  if (t === "patient_countered") return { label: "Patient countered", cls: "new" };
  if (t === "patient_pending") return { label: "Pending", cls: "pending" };
  if (t === "admin_message") return { label: "Admin", cls: "admin" };
  if (t === "chat_message") return { label: "Message", cls: "info" };
  if (t === "admin_rating") return { label: "Rating", cls: "admin" };
  if (t.includes("appointment") || t.includes("booking")) return { label: "Appointment", cls: "info" };
  if (t.includes("prescription")) return { label: "Prescription", cls: "info" };
  return { label: type || "Info", cls: "info" };
}

function EmptyState({ hasQuery, onClearSearch, onGoDashboard }) {
  return (
    <div className="doc_notif_empty" role="status" aria-live="polite">
      <span className="doc_notif_empty_icon" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
          <path d="M9.5 19a2.5 2.5 0 0 0 5 0" />
        </svg>
      </span>
      <h2 className="doc_notif_empty_title">
        {hasQuery ? "No matching notifications" : "You're all caught up"}
      </h2>
      <p className="doc_notif_empty_text">
        {hasQuery
          ? "Try a different keyword or clear the search to see all notifications."
          : "New requests, bid updates, and admin messages will appear here as soon as they arrive."}
      </p>
      <div className="doc_notif_empty_actions">
        {hasQuery ? (
          <button type="button" className="doc_notif_clear" onClick={onClearSearch}>
            Clear search
          </button>
        ) : (
          <button type="button" className="doc_notif_btn" onClick={onGoDashboard}>
            Go to dashboard
          </button>
        )}
      </div>
    </div>
  );
}

export default function DoctorNotifications() {
  const isHospital = (() => {
    try {
      return String(window?.location?.pathname || "").toLowerCase().startsWith("/hospital");
    } catch (_) {
      return false;
    }
  })();
  const dashboardPath = isHospital ? "/hospital-dashboard" : "/doctor-dashboard";
  const notificationsPath = isHospital ? "/hospital-notifications" : "/doctor-notifications";
  const chatsPath = isHospital ? "/hospital-chats" : "/doctor-chats";
  const VIEWED_STORAGE_KEY = isHospital
    ? "healbid_hospital_notifications_viewed_at"
    : "healbid_doctor_notifications_viewed_at";
  const VIEWED_EVENT = isHospital
    ? "healbid_hospital_notifications_viewed"
    : "healbid_doctor_notifications_viewed";
  const notificationsEndpoint = isHospital
    ? ENDPOINTS.HOSPITAL_NOTIFICATIONS
    : ENDPOINTS.DOCTOR_NOTIFICATIONS;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const socketRef = React.useRef(null);
  const listRef = React.useRef([]);
  const [sessionViewedAt] = useState(() => {
    try {
      const s = localStorage.getItem(VIEWED_STORAGE_KEY);
      return s ? Number(s) : 0;
    } catch {
      return 0;
    }
  });

  const query = (searchParams.get("search") || "").trim().toLowerCase();

  const fetchNotifications = React.useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    API.get(notificationsEndpoint, { params: { page: 1, limit: 100 } })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res?.data?.data ?? res?.data) ? (res?.data?.data ?? res?.data) : [];
        setList(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Failed to load notifications");
          setList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [notificationsEndpoint]);

  useEffect(() => {
    const cleanup = fetchNotifications();
    return cleanup;
  }, [fetchNotifications]);

  useEffect(() => {
    listRef.current = list;
  }, [list]);

  useEffect(() => {
    return () => {
      try {
        const data = listRef.current;
        const maxTs = data.reduce((max, n) => {
          const t = n.updatedAt || n.createdAt;
          if (!t) return max;
          const ts = new Date(t).getTime();
          return isNaN(ts) ? max : Math.max(max, ts);
        }, 0);
        const viewedAt = Math.max(Date.now(), maxTs + 1);
        localStorage.setItem(VIEWED_STORAGE_KEY, String(viewedAt));
        window.dispatchEvent(new CustomEvent(VIEWED_EVENT, { detail: viewedAt }));
      } catch (_) {}
    };
  }, [VIEWED_EVENT, VIEWED_STORAGE_KEY]);

  // Socket.IO realtime: when notifications are emitted, refetch. Fallback: poll every 10s if socket unavailable.
  useEffect(() => {
    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(() => {
        try {
          fetchNotifications();
        } catch (_) {}
      }, 10000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: isHospital ? "hospital" : "doctor" });
    if (!socket) {
      startPolling();
      return () => stopPolling();
    }
    socketRef.current = socket;

    const onNotification = () => {
      try {
        fetchNotifications();
      } catch (_) {}
    };
    socket.on("notification:new", onNotification);
    socket.on("bid:update", onNotification);
    socket.on("connect", stopPolling);
    socket.on("disconnect", startPolling);
    socket.on("connect_error", startPolling);
    if (!socket.connected) startPolling();
    return () => {
      try {
        socket.off("notification:new", onNotification);
        socket.off("bid:update", onNotification);
        socket.off("connect", stopPolling);
        socket.off("disconnect", startPolling);
        socket.off("connect_error", startPolling);
      } catch (_) {}
      stopPolling();
    };
  }, [fetchNotifications, isHospital]);

  const filtered = useMemo(() => {
    if (!query) return list;
    return list.filter((n) => {
      const title = String(n.title || "").toLowerCase();
      const body = String(n.body || "").toLowerCase();
      const patientName = String(n.patientName || "").toLowerCase();
      const type = String(n.type || "").toLowerCase();
      return title.includes(query) || body.includes(query) || patientName.includes(query) || type.includes(query);
    });
  }, [list, query]);

  return (
    <DoctorLayout>
      <div className="doc_notif_page">
        {error && <div className="doc_notif_error">{error}</div>}

        {!loading && !error && query && (
          <div className="doc_notif_meta">
            Showing <strong>{filtered.length}</strong> of <strong>{list.length}</strong> notifications.
            <button
              type="button"
              className="doc_notif_clear"
              onClick={() => navigate(notificationsPath, { replace: true })}
            >
              Clear search
            </button>
          </div>
        )}

        {loading ? (
          <ul className="doc_notif_list" aria-label="Loading notifications">
            {Array.from({ length: 6 }).map((_, idx) => (
              <li key={`doc-notif-skel-${idx}`} className="doc_notif_card">
                <div className="doc_notif_top" style={{ gap: 10 }}>
                  <SkeletonBlock height={18} width={90} />
                  <SkeletonBlock height={14} width={70} />
                </div>
                <div style={{ marginTop: 10 }}>
                  <SkeletonBlock height={16} width="55%" />
                </div>
                <div style={{ marginTop: 10 }}>
                  <SkeletonText lines={2} lineHeight={12} gap={8} />
                </div>
                <div className="doc_notif_footer" style={{ marginTop: 12 }}>
                  <div className="doc_notif_patient" style={{ gap: 10, width: "100%" }}>
                    <SkeletonBlock height={28} width={28} className="doc_notif_avatar" />
                    <SkeletonBlock height={14} width="30%" />
                  </div>
                  <SkeletonBlock height={32} width={90} />
                </div>
              </li>
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <EmptyState
            hasQuery={Boolean(query)}
            onClearSearch={() => navigate(notificationsPath, { replace: true })}
            onGoDashboard={() => navigate(dashboardPath)}
          />
        ) : (
          <ul className="doc_notif_list">
            {filtered.map((n, idx) => {
              const badge = getTypeBadge(n.type);
              const target = getProviderNotificationPathOrDefault(n, {
                isHospital,
                dashboardPath,
              });
              const primaryLabel = target.includes(chatsPath)
                ? "Open chat"
                : target === notificationsPath
                  ? "View details"
                  : target.includes("appointments")
                    ? "Appointments"
                    : target.includes("settings")
                      ? "Open settings"
                      : "Open dashboard";
              return (
                <li
                  key={`${n.providerNotificationId || n.messageId || n.bidLogId || n.type}-${idx}`}
                  className={`doc_notif_card${
                    isNotificationUnread(n, sessionViewedAt) ? " doc_notif_card--unread" : ""
                  }`}
                >
                  <div className="doc_notif_top">
                    <span className={`doc_notif_badge ${badge.cls}`}>{badge.label}</span>
                    <span className="doc_notif_time">{formatTimeAgo(n.updatedAt || n.createdAt)}</span>
                  </div>
                  <div className="doc_notif_title">{n.title || n.type}</div>
                  <div className="doc_notif_body">{n.body || "—"}</div>
                  <div className="doc_notif_footer">
                    <div className="doc_notif_patient">
                      <span className="doc_notif_avatar" aria-hidden>
                        {(n.patientName ? n.patientName.charAt(0) : "P").toUpperCase()}
                      </span>
                      <span className="doc_notif_name">{n.patientName || "Patient"}</span>
                    </div>
                    <button type="button" className="doc_notif_btn" onClick={() => navigate(target)}>
                      {primaryLabel}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DoctorLayout>
  );
}

